import TableRow, {TableRowProps} from '@material-ui/core/TableRow'
import {useLocalTime} from 'lib/date-time'
import {MoreActionsMenu} from 'lib/ui/Menu'
import {colors} from 'lib/ui/theme'
import {
  Broadcast,
  Segment,
} from 'organization/Event/Broadcasts/BroadcastsProvider'
import {
  ActionTableCell,
  CenteredTableCell,
  ChannelTableCell,
  DateTableCell,
  NameTableCell,
  SegmentTableCell,
} from 'organization/Event/Broadcasts/BroadcastsTable'
import DeleteMenuItem from 'organization/Event/Broadcasts/BroadcastsTable/DeleteMenuItem'
import EditMenuItem from 'organization/Event/Broadcasts/BroadcastsTable/EditMenuItem'
import React, {useState} from 'react'
import {Draggable} from 'react-beautiful-dnd'
import styled from 'styled-components'
import DuplicateMenuItem from 'organization/Event/Broadcasts/BroadcastsTable/DuplicateMenuItem'
import TestMenuItem from 'organization/Event/Broadcasts/BroadcastsTable/TestMenuItem'
import {communicationsApi} from 'lib/url'
import {useEvent} from 'Event/EventProvider'
import {
  BroadcastEmailStatistic,
  StatisticsForEmailBroadcast,
} from 'organization/Event/Broadcasts/StatisticsForEmailBroadcast'
import Popover from '@material-ui/core/Popover'
import IconButton from '@material-ui/core/IconButton'
import PollOutlinedIcon from '@material-ui/icons/PollOutlined'

export default function BroadcastRow(props: {
  broadcast: Broadcast
  index: number
  onSuccess: (message: string) => void
}) {
  const {broadcast, index, onSuccess} = props
  const localTime = useLocalTime()

  return (
    <Draggable draggableId={`${broadcast.id}`} index={index}>
      {(provided, snapshot) => (
        <StyledTableRow
          aria-label="broadcast"
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
        >
          <DateTableCell>{localTime(broadcast.send_at)}</DateTableCell>
          <NameTableCell>{broadcast.name}</NameTableCell>
          <ChannelTableCell>
            {broadcast.channel?.name || 'N/A'}
          </ChannelTableCell>
          <SegmentTableCell>
            <Segments broadcast={broadcast} />
          </SegmentTableCell>
          <CenteredTableCell>
            <SendingStatus broadcast={broadcast} />
          </CenteredTableCell>
          <CenteredTableCell>
            <PublishedStatus broadcast={broadcast} />
          </CenteredTableCell>
          <ActionTableCell align="right">
            <BroadcastStatistics broadcast={broadcast} />
            <BroadcastActions broadcast={broadcast} onSuccess={onSuccess} />
          </ActionTableCell>
        </StyledTableRow>
      )}
    </Draggable>
  )
}

/**
 * Cells must have explicit widths, otherwise they will
 * shrink when being dragged. Also need to set row
 * display to 'table' while dragging.
 *
 * Reference: https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/patterns/tables.md
 */
const StyledTableRow = styled(
  React.forwardRef<
    HTMLTableRowElement,
    {
      isDragging?: boolean
    } & TableRowProps
  >(({isDragging: _, ...otherProps}, ref) => (
    <TableRow {...otherProps} ref={ref} />
  )),
)`
  background: #ffffff;
  display: ${(props) => (props.isDragging ? 'table' : 'table-row')};
`

function Segments(props: {broadcast: Broadcast}) {
  const {broadcast} = props
  const segments = [broadcast.attendee_segment] as Segment[]

  return (
    <>
      {segments.map((s) => (
        <div key={s}>{segmentLabel(s)}</div>
      ))}
    </>
  )
}

function BroadcastStatistics(props: {broadcast: Broadcast}) {
  const {broadcast} = props

  if (broadcast.channel.name === 'sms') {
    return null
  }

  if (broadcast.channel.name === 'email') {
    return <EmailStatitics broadcast={broadcast} />
  }

  return null
}

function EmailStatitics(props: {broadcast: Broadcast}) {
  const {broadcast} = props
  const {client} = useEvent()
  const [
    emailStatistics,
    setEmailStatistics,
  ] = useState<BroadcastEmailStatistic | null>(null)

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const DEFAULT_EMAIL_STATISTICS: BroadcastEmailStatistic = {
    num_sent: 0,
    num_opened: 0,
    num_clicked: 0,
    num_complaints: 0,
    num_bounced: 0,
  }

  const open = (event: React.MouseEvent) => {
    setEmailStatistics(null)
    loadStatistics()
    setAnchorEl(event.currentTarget)
  }

  const loadStatistics = () => {
    const url = communicationsApi(`/broadcasts/${broadcast.id}/statistics`)

    client
      .get<{
        email: BroadcastEmailStatistic
      }>(url)
      .then(({email}) => {
        setEmailStatistics(email || DEFAULT_EMAIL_STATISTICS)
      })
  }

  const close = () => {
    setAnchorEl(null)
  }

  const isOpen = Boolean(anchorEl)
  return (
    <>
      <IconButton
        aria-haspopup="true"
        aria-label="show statistics"
        onClick={open}
      >
        <PollOutlinedIcon />
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <StatisticsForEmailBroadcast statistics={emailStatistics} />
      </Popover>
    </>
  )
}

function segmentLabel(segment: Segment) {
  switch (segment) {
    case 'all':
      return 'All Attendees'
    case 'checked_in':
      return 'Checked In'
    case 'not_checked_in':
      return 'Not Checked In'
    case 'filtered':
      return 'Filtered'
    default:
      throw new Error(`Missing segment label for segment: ${segment}`)
  }
}

function BroadcastActions(props: {
  broadcast: Broadcast
  onSuccess: (message: string) => void
}) {
  const {broadcast, onSuccess} = props

  return (
    <MoreActionsMenu>
      <TestMenuItem broadcast={broadcast} onSuccess={onSuccess} />
      <EditMenuItem broadcast={broadcast} />
      <DuplicateMenuItem broadcast={broadcast} />
      <DeleteMenuItem broadcast={broadcast} />
    </MoreActionsMenu>
  )
}

function SendingStatus(props: {broadcast: Broadcast}) {
  const {broadcast} = props
  const iconClass = broadcast.sent ? 'fas' : 'fal'
  const title = broadcast.sent ? 'has completed' : 'is pending'

  return (
    <StyledSendingIcon
      aria-label={`broadcast send status ${
        broadcast.sent ? 'sent' : 'pending'
      }`}
      className={`${iconClass} fa-circle`}
      title={`Broadcast send ${title}`}
    />
  )
}

function PublishedStatus(props: {broadcast: Broadcast}) {
  const {broadcast} = props
  const iconClass = broadcast.is_draft ? 'fal' : 'fas'
  const title = broadcast.is_draft ? 'draft' : 'published'

  return (
    <StyledSendingIcon
      aria-label={`broadcast status ${
        broadcast.is_draft ? 'draft' : 'published'
      }`}
      className={`${iconClass} fa-circle`}
      title={`Broadcast is ${title}`}
    />
  )
}

const StyledSendingIcon = styled.i`
  color: ${colors.primary};
`
